import React, { Suspense } from 'react';
import { FormOptionsContext, SubmitError } from '$/components/ui/Form';
import {
  AssetInput,
  MediaRef,
  MediaRefFragment,
  useCreateUploadUrlMutation,
  useUploadMediaRefMutation,
} from '$/GraphQL/schema/schema';
import useFormImageUpload from '$/hooks/useFormImageUpload';
import { css } from '@emotion/react';
import { LocalAsset, PageMode } from '$/components/ui/Input/PageBuilder.types';
import tw from 'twin.macro';
import stripTypenames from '$/utils/stripTypenames';
import { isValidURL } from '$/utils/isUrl';
import { InteractionBarInfo } from '../ImageOrVideoModal/ImageOrVideoModal';
import { useFormContext } from 'react-hook-form';

const ImageOrVideoModal = React.lazy(() => import('../ImageOrVideoModal/ImageOrVideoModal'));

/**
 * Prepares the asset input for the upload mutation.
 * @param asset The local asset to prepare.
 * @returns The prepared asset input: entity, entityId, name, and container.
 */
const prepareAssetInput = (asset: LocalAsset): AssetInput => {
  return {
    entity: asset.entity,
    entityId: asset.entityId,
    name: asset.name,
    container: asset.container,
  };
};

export const isValidImage = (file: File): File | undefined =>
  ['image/jpg', 'image/png', 'image/jpeg'].indexOf(file.type) !== -1 ? file : undefined;
export interface WithUploadChildrenProps {
  loading: boolean;
  value: MediaRefFragment | null | undefined;
  error: string | null;
  onRemove: () => void;
  onChange?: (url: MediaRef | null) => void;
}

export interface WithUploadProps {
  className?: string;
  name: string;
  asset: LocalAsset;
  allowThirdPartyURLs?: boolean;
  interactionBar?: InteractionBarInfo;
  avatar?: boolean;
  disabled?: boolean;
  value: MediaRefFragment | null | undefined;
  allowVideo?: boolean;
  mode?: PageMode;
  showZoomControls?: boolean;
  onChange?: (val: MediaRef | null | undefined) => void;
  children: (props: WithUploadChildrenProps) => React.ReactNode;
}

export interface WithUploadImageRef {
  toggleModal: (isOpen: boolean, value?: MediaRef) => void;
}

const DisableSubmitWhenUploading: React.FC<{
  fieldName: string;
  isUploading: boolean;
  skip: boolean;
}> = React.memo(({ fieldName, isUploading, skip }) => {
  // disable submit button when uploading
  useFormImageUpload({
    fieldName,
    isUploading,
    skip,
  });
  return null;
});
DisableSubmitWhenUploading.displayName = 'DisableSubmitWhenUploading';

const updateMediaPermanent = (
  obj: Record<string, unknown>,
  oldUrl: string,
  newMediaRef: MediaRef
): Record<string, unknown> => {
  if (!obj || typeof obj !== 'object') return obj;

  const result = { ...obj };

  Object.entries(obj).forEach(([key, value]) => {
    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        result[key] = value.map((item) =>
          typeof item === 'object'
            ? updateMediaPermanent(item as Record<string, unknown>, oldUrl, newMediaRef)
            : item
        );
      } else {
        if (key.endsWith('MediaRef')) {
          const mediaRef = value as MediaRef;
          if (mediaRef.url === oldUrl) {
            result[key] = newMediaRef;
          }
        } else {
          result[key] = updateMediaPermanent(value as Record<string, unknown>, oldUrl, newMediaRef);
        }
      }
    } else if (typeof value === 'string' && value.includes(oldUrl)) {
      const escapedOldUrl = oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedOldUrl, 'g');
      result[key] = value.replace(regex, newMediaRef.imageUrl);
    }
  });

  return result;
};

export const WithUploadImage = React.forwardRef<WithUploadImageRef, WithUploadProps>(
  (
    {
      value,
      name,
      asset: localAsset,
      disabled,
      onChange,
      className,
      children,
      allowVideo,
      mode = PageMode.edit,
      avatar,
      allowThirdPartyURLs,
      interactionBar,
      showZoomControls,
    },
    ref
  ) => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [createUploadUrlMutation] = useCreateUploadUrlMutation();
    const [uploadMediaRefMutation] = useUploadMediaRefMutation();
    const { addPendingUpload, removePendingUpload } = React.useContext(FormOptionsContext);
    const formHandlers = useFormContext();
    const [showModal, setShowModal] = React.useState(false);
    const onClose = React.useCallback(() => {
      setShowModal(false);
    }, []);

    const mediaValue: MediaRef | undefined = stripTypenames(value) ?? undefined;

    React.useImperativeHandle(ref, () => ({
      toggleModal: (showModal): void => {
        setShowModal(showModal);
      },
    }));

    const onChangeMedia = React.useCallback(
      (value: MediaRefFragment | null): void => {
        if (!onChange) return;

        if (!value) {
          onChange(null);
          return;
        }

        // Remove remnant from MediaPreview type
        delete (value as any).type;
        onChange(value);
      },
      [onChange]
    );

    const onRemove = React.useCallback(() => {
      removePendingUpload?.(name);
      onChangeMedia?.(null);
    }, [onChangeMedia, removePendingUpload, name]);

    type UploadInput = File | (() => Promise<File>);

    const queueUpload = React.useCallback(
      (getFile: () => Promise<File>, newValue: MediaRefFragment) => {
        // Remove any existing pending upload for this field
        removePendingUpload?.(name);

        // Immediately update the UI with the preview
        onChangeMedia?.(newValue);

        // Queue the actual upload for form submission
        addPendingUpload?.(
          name,
          async (values: Record<string, unknown>): Promise<Record<string, unknown>> => {
            try {
              setLoading(true);
              const file = await getFile();
              const assetInput = prepareAssetInput(localAsset);

              // If the file is a URL, download it first
              let fileToUpload = file;
              if (typeof file === 'string' && isValidURL(file)) {
                const response = await fetch(file);
                const blob = await response.blob();
                fileToUpload = new File([blob], 'downloaded-file', { type: blob.type });
              }

              const response = await createUploadUrlMutation({
                variables: {
                  data: {
                    ...assetInput,
                    image: {
                      width: newValue.width!,
                      height: newValue.height!,
                      type: fileToUpload.type,
                    },
                  },
                },
              });

              if (!response.data?.createAssetUploadUrl?.uploadUrl) {
                throw new Error('Unable to upload image');
              }
              const { url, uploadUrl } = response.data!.createAssetUploadUrl!;

              await fetch(uploadUrl!, {
                method: 'PUT',
                body: fileToUpload,
                headers: {
                  'Content-Type': fileToUpload.type,
                },
              });

              // Generate the final mediaRef with imgix URL
              const mediaRef: MediaRef = {
                ...newValue,
                url: url, // Always use the imgix URL as the final URL
                imageUrl: url, // Store the imgix URL
              };

              // If we don't have dimensions, try to get them
              if (!mediaRef.width) {
                const img = new Image();
                await new Promise<void>((resolve, reject) => {
                  img.onload = () => {
                    mediaRef.width = img.width;
                    mediaRef.height = img.height;
                    resolve();
                  };
                  img.onerror = () => reject(new Error('Unable to load image'));
                  img.src = mediaRef.url;
                });
              }

              onChangeMedia?.(mediaRef);

              if (newValue.url !== mediaRef.url) {
                values = updateMediaPermanent(values, newValue.url, mediaRef);
              }

              formHandlers.clearErrors(name);

              return values;
            } catch (e) {
              console.error(e);
              const errorMsg = 'Unable to upload image';
              setError(errorMsg);
              formHandlers.setError(name, { type: 'error', message: errorMsg });
              throw new SubmitError({ [name]: errorMsg });
            } finally {
              setLoading(false);
            }
          }
        );
      },
      [
        onChangeMedia,
        addPendingUpload,
        name,
        removePendingUpload,
        localAsset,
        createUploadUrlMutation,
        formHandlers,
      ]
    );

    const queueUploadMediaRef = React.useCallback(
      (mediaRef: MediaRefFragment) => {
        // Remove any existing pending upload for this field
        removePendingUpload?.(name);

        // Immediately update the UI with the preview
        onChangeMedia?.(mediaRef);

        // Queue the actual upload for form submission
        addPendingUpload?.(name, async (values: Record<string, unknown>) => {
          try {
            setLoading(true);
            const assetInput = prepareAssetInput(localAsset);

            const response = await uploadMediaRefMutation({
              variables: {
                data: {
                  asset: assetInput,
                  mediaRef: stripTypenames(mediaRef),
                },
              },
            });

            if (!response.data?.uploadMediaRef) {
              throw new Error('Unable to upload image');
            }

            const uploadedMediaRef = response.data.uploadMediaRef;

            onChangeMedia?.(uploadedMediaRef);

            if (mediaRef.url !== uploadedMediaRef.url) {
              values = updateMediaPermanent(values, mediaRef.url, uploadedMediaRef);
            }

            formHandlers.clearErrors(name);
            return values;
          } catch (e) {
            console.error(e);
            const errorMsg = 'Unable to upload image';
            setError(errorMsg);
            formHandlers.setError(name, { type: 'error', message: errorMsg });
            throw new SubmitError({ [name]: errorMsg });
          } finally {
            setLoading(false);
          }
        });
      },
      [
        onChangeMedia,
        addPendingUpload,
        name,
        removePendingUpload,
        localAsset,
        uploadMediaRefMutation,
        formHandlers,
      ]
    );

    const handleUpload = React.useCallback(
      async (fileOrGetter: UploadInput | undefined, newValue: MediaRefFragment): Promise<void> => {
        // If newValue is undefined, then fileOrGetter is actually a MediaRefFragment for direct upload
        if (fileOrGetter) {
          const getFile =
            typeof fileOrGetter === 'function'
              ? fileOrGetter
              : () => Promise.resolve(fileOrGetter as File);
          queueUpload(getFile, newValue);
        } else {
          queueUploadMediaRef(newValue);
        }
      },
      [queueUpload, queueUploadMediaRef]
    );

    return (
      <>
        <DisableSubmitWhenUploading
          fieldName={name}
          isUploading={loading}
          skip={Boolean(disabled)}
        />
        <div
          className={`flex ${className}`}
          onClick={(e): void => {
            if (mode === PageMode.preview) return;
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
          }}
          css={[
            mode === PageMode.edit
              ? css`
                  .image-show {
                    ${tw`relative z-1`}
                    &::after {
                      ${tw`absolute top-0 left-0 text-white justify-center w-full h-full items-center hidden cursor-pointer`}
                      background: rgba(0, 0, 0, 0.3);
                      backdrop-filter: blur(1px);
                      z-index: 10;
                      content: '';
                      border-radius: ${avatar ? '100' : '0'}%;
                    }
                    &::before {
                      ${tw`absolute top-0 left-0 bottom-0 right-0 text-white m-auto hidden cursor-pointer`}
                      content: '${value?.url ? 'Edit' : 'Upload'}';
                      font-size: 0.8rem;
                      padding: 0.2rem 0.8rem;
                      background: rgba(0, 0, 0, 0.5);
                      backdrop-filter: blur(1px);
                      z-index: 11;
                      max-width: 4rem;
                      min-width: 2rem;
                      height: max-content;
                      border-radius: 2rem;
                    }
                    &:hover {
                      &::before,
                      &::after {
                        ${tw`flex items-center justify-center`}
                      }
                    }
                    &.larger {
                      &::after {
                        border-radius: 1rem;
                      }
                      &::before {
                        font-size: 1.3rem !important;
                        padding: 0.5rem 2.5rem;
                      }
                    }
                  }
                `
              : '',
          ]}
        >
          {children({ loading, value: mediaValue, onChange, error, onRemove })}
        </div>
        {!disabled && showModal && (
          <Suspense>
            <ImageOrVideoModal
              error={error}
              allowThirdPartyURLs={allowThirdPartyURLs}
              interactionBar={interactionBar}
              onClose={onClose}
              asset={localAsset}
              setError={setError}
              allowVideo={allowVideo}
              value={value}
              onChange={onChangeMedia}
              handleUpload={handleUpload}
              setLoading={setLoading}
              showZoomControls={showZoomControls}
            />
          </Suspense>
        )}
      </>
    );
  }
);
WithUploadImage.displayName = 'WithUploadImage';
